import React, {ReactNode} from 'react'

import {FormControl, FormHelperText, InputLabel, OutlinedInput, Select, makeStyles} from "@material-ui/core";
import clsx from "clsx";
import { getSelectorMenuLayout } from './menuLayout';

const useStyles = makeStyles({
    menuList: {
        paddingTop: 0,
        paddingBottom: 0,
        '& [role="option"]': {
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
        },
    },
});

interface MaterialSelectorProps {
    id?: string
    name: string
    label: string
    value?: string | number
    className?: string
    errorMessage?: string
    children: ReactNode
    defaultValue?: string | number
    onChange?: (e: any) => void
    onBlur?: (e: any) => void
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void
    disabled?: boolean
    focused?: boolean
    startAdornmentIcon?: React.ReactNode
    placeholder?: string
    formatSelectedValue?: (selected: string | number) => React.ReactNode
    /** Keeps long menus clear of app-owned sticky chrome on small viewports. */
    menuViewportMargin?: number
}

const MaterialSelector = ({ className, errorMessage, label, id, name, children, focused, disabled, value, startAdornmentIcon, placeholder, formatSelectedValue, menuViewportMargin = 16, onChange, ...props}: MaterialSelectorProps) => {
    const classes = useStyles();
    const anchorRef = React.useRef<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [menuLayout, setMenuLayout] = React.useState<ReturnType<typeof getSelectorMenuLayout> | null>(null);
    const updateMenuLayout = React.useCallback(() => {
        if (!anchorRef.current) return;
        setMenuLayout(getSelectorMenuLayout({
            anchor: anchorRef.current.getBoundingClientRect(),
            viewportWidth: window.visualViewport?.width || window.innerWidth,
            viewportHeight: window.visualViewport?.height || window.innerHeight,
            viewportTop: window.visualViewport?.offsetTop || 0,
            viewportLeft: window.visualViewport?.offsetLeft || 0,
            verticalMargin: menuViewportMargin,
        }));
    }, [menuViewportMargin]);

    React.useEffect(() => {
        if (!menuOpen) return;
        updateMenuLayout();
        window.addEventListener('resize', updateMenuLayout);
        window.addEventListener('scroll', updateMenuLayout, true);
        window.visualViewport?.addEventListener('resize', updateMenuLayout);
        window.visualViewport?.addEventListener('scroll', updateMenuLayout);
        return () => {
            window.removeEventListener('resize', updateMenuLayout);
            window.removeEventListener('scroll', updateMenuLayout, true);
            window.visualViewport?.removeEventListener('resize', updateMenuLayout);
            window.visualViewport?.removeEventListener('scroll', updateMenuLayout);
        };
    }, [menuOpen, updateMenuLayout]);
    const [hasInternalValue, setHasInternalValue] = React.useState(false);
    const shouldShrink = (value !== undefined && value !== null && value !== '') || !!placeholder || hasInternalValue;
    const handleChange = React.useCallback((e: any) => {
        setHasInternalValue(true);
        onChange?.(e);
    }, [onChange]);
    const getSelectedLabel = React.useCallback((selected: string | number): React.ReactNode | undefined => {
        if (formatSelectedValue) {
            return formatSelectedValue(selected);
        }

        let derivedLabel: React.ReactNode | undefined = undefined;
        const valuesEqual = (a: string | number, b: string | number) => String(a) === String(b);
        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child)) {
                const element = child as React.ReactElement<{ value: string | number; children: React.ReactNode }>;
                if (valuesEqual(element.props.value, selected)) {
                    derivedLabel = element.props.children;
                }
            }
        });

        return derivedLabel;
    }, [children, formatSelectedValue]);
    return (
        <FormControl disabled={disabled} variant="outlined" fullWidth error={!!errorMessage} focused={focused}>
            <InputLabel shrink={shouldShrink}>{label}</InputLabel>
            <Select
                className={clsx('no-fastclick', className)}
                style={{borderRadius: '0.5rem'}}
                MenuProps={{
                    marginThreshold: 16,
                    ...(menuLayout ? {
                        anchorReference: 'anchorPosition' as const,
                        anchorPosition: menuLayout.anchorPosition,
                        transformOrigin: menuLayout.transformOrigin,
                    } : {}),
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                        style: {
                            maxWidth: 'calc(100vw - 32px)',
                            maxHeight: menuLayout?.maxHeight,
                            width: menuLayout?.width,
                            minWidth: menuLayout?.width ?? 0,
                        },
                    },
                    MenuListProps: {
                        className: classes.menuList,
                        id: `${id || name}-menu`,
                    },
                }}
                SelectDisplayProps={{ style: { display: 'flex', alignItems: 'baseline', borderRadius: '0.5rem', whiteSpace: 'break-spaces' } }}
                name={name}
                id={id}
                value={value}
                input={<OutlinedInput label={label} notched={shouldShrink} style={{borderRadius: '0.5rem'}} />}
                renderValue={(selected) => {
                    const hasSelected = selected !== undefined && selected !== null && selected !== '';
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', color: hasSelected ? undefined : '#6d6d72' }}>
                            {startAdornmentIcon && (
                                <span style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                                    {startAdornmentIcon}
                                </span>
                            )}
                            {hasSelected && (typeof selected === 'string' || typeof selected === 'number') ? (getSelectedLabel(selected) ?? placeholder) : placeholder}
                        </div>
                    );
                }}
                displayEmpty
                onOpen={(event) => {
                    anchorRef.current = event.currentTarget as HTMLElement;
                    updateMenuLayout();
                    setMenuOpen(true);
                }}
                onClose={() => {
                    anchorRef.current = null;
                    setMenuOpen(false);
                }}
                onChange={handleChange}
                {...props}
            >
                {children}
            </Select>
            <FormHelperText>{!!errorMessage && errorMessage}</FormHelperText>
        </FormControl>
    )
}

export default MaterialSelector
